// iOS Settings-style grouped-inset list of catalog items.
// Each row carries an SF-Symbol-style icon by kind, the display name as
// the primary line, a secondary line with SKU + kind, and the price
// right-aligned as a trailing value — mirroring iOS Settings "Row with
// trailing value + chevron" affordance.

import { formatCents, type ItemSummary, labelForKind } from "../data/catalog.ts";
import { ChevronRightIcon, KindIcon } from "./icons.tsx";
import { kindTone } from "./utils.ts";

export type ListState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "loaded"; items: ItemSummary[]; hasMore: boolean }
  | { kind: "unauthorized" }
  | { kind: "error"; status: number; error: string };

export function ItemList({
  state,
  selectedId,
  onSelect,
  onLoadMore,
}: {
  state: ListState;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onLoadMore: () => void;
}) {
  if (state.kind === "loading" || state.kind === "idle") {
    return <EmptyState text="Loading" />;
  }
  if (state.kind === "unauthorized") {
    return (
      <EmptyState>
        <p className="text-[17px] text-slate-900 font-medium">Not available</p>
        <p className="text-[17px] text-slate-500 mt-1">Your session has ended.</p>
        <a className="mt-4 inline-block text-[17px] text-[#007aff] font-medium" href="#/sign-in">
          Sign in
        </a>
      </EmptyState>
    );
  }
  if (state.kind === "error") {
    return <EmptyState text={`${state.status} · ${state.error}`} tone="error" />;
  }
  if (state.items.length === 0) {
    return <EmptyState text="No items match." />;
  }
  return (
    <div className="px-4 md:px-6 pb-10">
      <div className="rounded-2xl bg-white border border-slate-200/70 overflow-hidden shadow-[0_0.5px_0_rgba(0,0,0,0.04)]">
        <ul>
          {state.items.map((item, i) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onSelect(item.id)}
                className={`w-full text-left flex items-center gap-3.5 pl-4 pr-3 py-3.5 min-h-[60px] transition-colors ${
                  selectedId === item.id ? "bg-slate-100" : "hover:bg-slate-50 active:bg-slate-100"
                }`}
                aria-label={`${item.displayName}, ${formatCents(item.defaultPriceCents)}, ${labelForKind(item.kind)}${item.restrictedFlag ? ", restricted" : ""}`}
              >
                <KindBadge kind={item.kind} />
                <div className="flex-1 min-w-0">
                  <p className="text-[17px] text-slate-900 font-medium leading-tight truncate">
                    {item.displayName}
                  </p>
                  <p className="text-[15px] text-slate-500 mt-0.5 truncate">
                    <span className="font-mono tracking-tight">{item.sku}</span>
                    <span className="mx-1.5 text-slate-300">·</span>
                    {labelForKind(item.kind)}
                    {item.restrictedFlag && (
                      <>
                        <span className="mx-1.5 text-slate-300">·</span>
                        <span className="text-[#ff3b30] font-medium">Restricted</span>
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[17px] text-slate-500 tabular-nums">
                    {formatCents(item.defaultPriceCents)}
                  </span>
                  <ChevronRightIcon className="text-slate-300" />
                </div>
              </button>
              {i < state.items.length - 1 && <div className="ml-[64px] h-px bg-slate-200/80" />}
            </li>
          ))}
        </ul>
      </div>
      {state.hasMore && (
        <div className="pt-5 text-center">
          <button
            type="button"
            onClick={onLoadMore}
            className="text-[17px] text-[#007aff] font-medium"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

// iOS Settings icon-tile: 32×32 rounded square with the kind color as its
// fill and a white glyph on top. Visually chunkier than the previous dot
// and recognizable at a glance across the floor.
function KindBadge({ kind }: { kind: string }) {
  return (
    <span
      className={`h-9 w-9 rounded-[9px] shrink-0 grid place-items-center text-white ${kindTone(kind)}`}
      aria-hidden="true"
    >
      <KindIcon kind={kind} className="w-5 h-5" />
    </span>
  );
}

function EmptyState({
  text,
  children,
  tone,
}: {
  text?: string;
  children?: React.ReactNode;
  tone?: "error";
}) {
  const cls = tone === "error" ? "text-[#ff3b30]" : "text-slate-400";
  return (
    <div className="px-6 pt-16 pb-10 text-center">
      {children ?? <p className={`text-[17px] ${cls}`}>{text}</p>}
    </div>
  );
}
